import API from "serviceshift-ui/api-client";

import CONSTANTS from "@/Constants.js";
import store from "@/vuex/store";
const { VIDEO_UPLOAD_SOURCE, VIDEO_MAX_SIZE, VIDEO_CHUNK_SIZE } = CONSTANTS;

export interface VideoFile {
  file: File;
  fileName: string;
}

export interface FailedUpload extends VideoFile {
  reason: "too_large" | "upload_error";
}

export interface UploadProgress {
  progress: number;
  currentFile: number;
  totalFiles: number;
  currentChunk: number;
  totalChunks: number;
}

export interface CompletedPart {
  part_number: number;
  etag: string;
}

export default class VideoUpload {
  uploadSource: string;
  maxSize: number;
  chunkSize: number;
  jobId: number;
  success: number;
  failures: number;
  videos: VideoFile[] = [];
  onProgress: ((progress: UploadProgress) => void) | null = null;
  cancelled = false;
  failedUploads: FailedUpload[] = [];

  private currentUploadKey: string | null = null;
  private currentUploadId: string | null = null;

  constructor(params: {
    jobId: number;
    videos: VideoFile[];
    uploadSource?: string;
    maxSize?: number;
    chunkSize?: number;
  }) {
    this.jobId = params.jobId;
    this.videos = params.videos || [];
    this.uploadSource = params.uploadSource || VIDEO_UPLOAD_SOURCE;
    this.maxSize = params.maxSize || VIDEO_MAX_SIZE;
    this.chunkSize = params.chunkSize || VIDEO_CHUNK_SIZE;
    this.success = 0;
    this.failures = 0;
  }

  async uploadAll(): Promise<void> {
    if (this.cancelled) return;

    this.resetUploader();

    for (let i = 0; i < this.videos.length; i++) {
      if (this.cancelled) break;

      const video = this.videos[i];

      if (video.file.size > this.maxSize) {
        this.logFailure(video, "too_large");
        continue;
      }

      try {
        await this.uploadVideo(video, i);
        this.success++;
      } catch (error) {
        console.error("Video upload failed:", error);
        this.logFailure(video, "upload_error");

        // Attempt to abort the multipart upload if it was initiated
        if (this.currentUploadKey && this.currentUploadId) {
          try {
            await this.abortUpload();
          } catch (abortError) {
            console.error("Failed to abort upload:", abortError);
          }
        }
      }
    }
  }

  private async uploadVideo(
    video: VideoFile,
    fileIndex: number
  ): Promise<void> {
    const extension = this.getFileExtension(video.fileName);
    const contentType = video.file.type || "video/mp4";

    // Step 1: Initiate multipart upload
    const { key, upload_id } = await this.initiateUpload(
      extension,
      contentType
    );
    this.currentUploadKey = key;
    this.currentUploadId = upload_id;

    // Step 2: Calculate chunks
    const totalChunks = Math.ceil(video.file.size / this.chunkSize);
    const completedParts: CompletedPart[] = [];

    // Step 3: Upload each chunk
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      if (this.cancelled) {
        throw new Error("Upload cancelled");
      }

      const partNumber = chunkIndex + 1;
      const start = chunkIndex * this.chunkSize;
      const end = Math.min(start + this.chunkSize, video.file.size);
      const chunk = video.file.slice(start, end);

      // Get presigned URL for this part
      const { parts } = await this.getPartUrls(key, upload_id, [partNumber]);
      const partUrl = parts[0].url;

      // Upload the chunk directly to S3
      const etag = await this.uploadChunk(partUrl, chunk);
      completedParts.push({ part_number: partNumber, etag });

      // Update progress
      this.updateProgress({
        progress:
          ((fileIndex + (chunkIndex + 1) / totalChunks) / this.videos.length) *
          100,
        currentFile: fileIndex + 1,
        totalFiles: this.videos.length,
        currentChunk: chunkIndex + 1,
        totalChunks
      });
    }

    // Step 4: Complete the multipart upload
    await this.completeUpload(key, upload_id, completedParts, video.fileName);

    // Clear current upload tracking
    this.currentUploadKey = null;
    this.currentUploadId = null;
  }

  private async initiateUpload(
    extension: string,
    contentType: string
  ): Promise<{ key: string; upload_id: string }> {
    return store.dispatch("initiateMultipartUpload", {
      jobId: this.jobId,
      extension,
      contentType
    });
  }

  private async getPartUrls(
    key: string,
    uploadId: string,
    partNumbers: number[]
  ): Promise<{ parts: Array<{ part_number: number; url: string }> }> {
    return store.dispatch("getUploadPartUrls", {
      key,
      uploadId,
      partNumbers
    });
  }

  private async uploadChunk(url: string, chunk: Blob): Promise<string> {
    const response = await API.makeRequest(url, {
      method: "PUT",
      data: chunk,
      headers: {
        "Content-Type": "application/octet-stream",
        ignoreError: true
      }
    });

    // The ETag is returned in the response headers
    const etag = response.headers?.etag || response.headers?.ETag;
    if (!etag) {
      throw new Error("No ETag received from S3");
    }
    return etag;
  }

  private async completeUpload(
    key: string,
    uploadId: string,
    parts: CompletedPart[],
    filename: string
  ): Promise<void> {
    await store.dispatch("completeMultipartUpload", {
      jobId: this.jobId,
      key,
      uploadId,
      parts,
      source: this.uploadSource,
      filename
    });
  }

  private async abortUpload(): Promise<void> {
    if (this.currentUploadKey && this.currentUploadId) {
      await store.dispatch("abortMultipartUpload", {
        key: this.currentUploadKey,
        uploadId: this.currentUploadId
      });
    }
  }

  cancel(): void {
    this.cancelled = true;
    // Attempt to abort any in-progress upload
    if (this.currentUploadKey && this.currentUploadId) {
      this.abortUpload().catch(console.error);
    }
  }

  get size(): number {
    return this.videos.length;
  }

  private getFileExtension(fileName: string): string {
    const lastDot = fileName.lastIndexOf(".");
    return lastDot !== -1 ? fileName.substring(lastDot + 1).toLowerCase() : "";
  }

  private logFailure(video: VideoFile, reason: FailedUpload["reason"]): void {
    this.failures++;
    this.failedUploads.push({ ...video, reason });
  }

  private resetUploader(): void {
    this.cancelled = false;
    this.failedUploads = [];
    this.success = 0;
    this.failures = 0;
    this.currentUploadKey = null;
    this.currentUploadId = null;
  }

  private updateProgress(progress: UploadProgress): void {
    if (this.onProgress) {
      this.onProgress(progress);
    }
  }
}
